import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { PageHeader } from '../../components/layout/PageHeader';
import { ApiError } from '../../lib/api';
import { distinctTeams } from '../../lib/teams';
import { listAccounts } from '../accounts/api';
import type { TaskTemplateItem } from '../../types/taskTemplate';
import {
  createItem,
  deleteItem,
  deleteTemplate,
  getTemplate,
  listUsers,
  reorderItems,
  updateItem,
  updateTemplate,
  type ItemCreatePayload,
  type ItemUpdatePayload,
} from './api';
import {
  TaskTemplateItemForm,
  formatRelativeDue,
} from './TaskTemplateItemForm';

// Pure array reorder: move dragId to where targetId sits. Local to settings
// (the plans-module taskOrdering helper is behind that module's boundary).
function reorder(ids: string[], dragId: string, targetId: string): string[] {
  if (dragId === targetId) return ids;
  const next = ids.filter((id) => id !== dragId);
  const at = next.indexOf(targetId);
  if (at === -1) return ids;
  next.splice(at, 0, dragId);
  return next;
}

export function TaskTemplateDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const templateQuery = useQuery({
    queryKey: ['taskTemplate', id],
    queryFn: () => getTemplate(id!),
    enabled: !!id,
  });
  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: listAccounts,
  });
  const usersQuery = useQuery({
    queryKey: ['users', 'all'],
    queryFn: listUsers,
  });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [metaError, setMetaError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editingItem, setEditingItem] = useState<TaskTemplateItem | null>(
    null,
  );
  const [itemError, setItemError] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const template = templateQuery.data ?? null;

  // Seed the metadata draft once the template loads / changes.
  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description ?? '');
    }
  }, [template?.id, template?.name, template?.description]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['taskTemplate', id] });
    qc.invalidateQueries({ queryKey: ['taskTemplates'] });
  };

  const metaMut = useMutation({
    mutationFn: () =>
      updateTemplate(id!, {
        name: name.trim(),
        description: description.trim() || null,
      }),
    onSuccess: invalidate,
    onError: (err) =>
      setMetaError(err instanceof ApiError ? err.message : String(err)),
  });

  const addMut = useMutation({
    mutationFn: (payload: ItemCreatePayload) => createItem(id!, payload),
    onSuccess: () => {
      setShowAdd(false);
      invalidate();
    },
    onError: (err) =>
      setItemError(err instanceof ApiError ? err.message : String(err)),
  });

  const editMut = useMutation({
    mutationFn: ({
      itemId,
      patch,
    }: {
      itemId: string;
      patch: ItemUpdatePayload;
    }) => updateItem(id!, itemId, patch),
    onSuccess: () => {
      setEditingItem(null);
      invalidate();
    },
    onError: (err) =>
      setItemError(err instanceof ApiError ? err.message : String(err)),
  });

  const deleteItemMut = useMutation({
    mutationFn: (itemId: string) => deleteItem(id!, itemId),
    onSuccess: invalidate,
  });

  const reorderMut = useMutation({
    mutationFn: (itemIds: string[]) => reorderItems(id!, itemIds),
    onSuccess: invalidate,
  });

  const deleteTemplateMut = useMutation({
    mutationFn: () => deleteTemplate(id!),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['taskTemplates'] });
      qc.removeQueries({ queryKey: ['taskTemplate', id] });
      navigate('/settings');
    },
    onError: (err) =>
      setDeleteError(err instanceof ApiError ? err.message : String(err)),
  });

  if (templateQuery.isLoading) return <CenteredSpinner />;
  if (templateQuery.error || !template) {
    return (
      <div className="text-sm text-red-600">
        Template not found or failed to load.
      </div>
    );
  }

  const brand =
    template.scope === 'brand' && template.brandAccountId
      ? ((accountsQuery.data ?? []).find(
          (a) => a.id === template.brandAccountId,
        )?.name ?? template.brandAccountId)
      : null;
  const teams = distinctTeams(usersQuery.data ?? []);
  const items = template.items;
  const metaDirty =
    name.trim() !== template.name ||
    (description.trim() || null) !== (template.description ?? null);
  const busy =
    metaMut.isPending ||
    addMut.isPending ||
    editMut.isPending ||
    deleteItemMut.isPending ||
    reorderMut.isPending;

  const handleDrop = (targetId: string) => {
    if (!dragId) return;
    const orderedIds = items.map((i) => i.id);
    const next = reorder(orderedIds, dragId, targetId);
    setDragId(null);
    if (next.join(' ') !== orderedIds.join(' ')) reorderMut.mutate(next);
  };

  return (
    <div className="max-w-3xl space-y-6">
      <PageHeader
        title={template.name}
        backHref="/settings"
        actions={
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              setDeleteError(null);
              setShowDelete(true);
            }}
          >
            Delete template
          </Button>
        }
      />

      <section className="space-y-3 rounded-md border border-slate-200 bg-white p-6">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-500">
            Name
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-slate-500">
            Description
          </span>
          <textarea
            value={description}
            rows={2}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
        </label>
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">
            Scope:{' '}
            <span className="font-medium text-slate-700">
              {template.scope === 'global'
                ? 'Global'
                : `Brand: ${brand}`}
            </span>{' '}
            <span className="text-slate-400">(read-only after create)</span>
          </p>
          <Button
            type="button"
            onClick={() => {
              if (!name.trim()) {
                setMetaError('Name is required.');
                return;
              }
              setMetaError(null);
              metaMut.mutate();
            }}
            disabled={!metaDirty || metaMut.isPending}
          >
            {metaMut.isPending ? 'Saving…' : 'Save'}
          </Button>
        </div>
        {metaError && <p className="text-sm text-red-600">{metaError}</p>}
      </section>

      <section className="rounded-md border border-slate-200 bg-white p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Tasks</h2>
          <Button
            type="button"
            onClick={() => {
              setItemError(null);
              setShowAdd(true);
            }}
          >
            + Add Task
          </Button>
        </div>

        {items.length === 0 ? (
          <p className="text-sm italic text-slate-400">
            No task items yet. Click "+ Add Task" to build the template.
          </p>
        ) : (
          <ol className="space-y-2">
            {items.map((it, idx) => (
              <li
                key={it.id}
                draggable
                onDragStart={() => setDragId(it.id)}
                onDragEnd={() => setDragId(null)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  handleDrop(it.id);
                }}
                className={`flex items-start justify-between gap-3 rounded-md border p-3 ${
                  dragId === it.id
                    ? 'border-slate-400 opacity-60'
                    : 'border-slate-200'
                }`}
              >
                <div className="flex min-w-0 gap-3">
                  <span
                    aria-hidden
                    className="cursor-grab select-none text-slate-300"
                    title="Drag to reorder"
                  >
                    ⠿
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900">
                      {idx + 1}. {it.title}
                    </p>
                    {it.description && (
                      <p className="mt-0.5 whitespace-pre-wrap text-xs text-slate-600">
                        {it.description}
                      </p>
                    )}
                    <p className="mt-0.5 text-xs text-slate-500">
                      Due: {formatRelativeDue(it.relativeDueDays)}
                      {' · '}
                      Team: {it.defaultTeam ?? 'Unassigned'}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="!px-2 !py-1 text-xs"
                    onClick={() => {
                      setItemError(null);
                      setEditingItem(it);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="!border-red-300 !px-2 !py-1 text-xs !text-red-600 hover:!bg-red-50"
                    disabled={busy}
                    onClick={() => deleteItemMut.mutate(it.id)}
                  >
                    Delete
                  </Button>
                </div>
              </li>
            ))}
          </ol>
        )}
      </section>

      {showAdd && (
        <TaskTemplateItemForm
          item={null}
          teams={teams}
          busy={addMut.isPending}
          error={itemError}
          onCancel={() => setShowAdd(false)}
          onSubmit={(payload) =>
            addMut.mutate(payload as ItemCreatePayload)
          }
        />
      )}
      {editingItem && (
        <TaskTemplateItemForm
          item={editingItem}
          teams={teams}
          busy={editMut.isPending}
          error={itemError}
          onCancel={() => setEditingItem(null)}
          onSubmit={(patch) =>
            editMut.mutate({ itemId: editingItem.id, patch })
          }
        />
      )}

      {showDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-md border border-slate-200 bg-white p-5 shadow-xl">
            <h3 className="text-sm font-semibold text-slate-900">
              Delete template
            </h3>
            <p className="mt-2 text-sm text-slate-600">
              Delete <span className="font-medium">{template.name}</span> and
              its {items.length} task item
              {items.length === 1 ? '' : 's'}? This can't be undone.
            </p>
            {template.usage > 0 && (
              <p className="mt-2 text-xs text-slate-500">
                Used {template.usage} time
                {template.usage === 1 ? '' : 's'}. Tasks already applied to
                Plans are independent snapshots and won't be affected.
              </p>
            )}
            {deleteError && (
              <p className="mt-2 text-sm text-red-600">{deleteError}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDelete(false)}
                disabled={deleteTemplateMut.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => deleteTemplateMut.mutate()}
                disabled={deleteTemplateMut.isPending}
                variant="destructive"
              >
                {deleteTemplateMut.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
